import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';
import authService from '../services/authService';
import testService from '../services/testService';
import surveyService from '../services/surveyService';
import MasterEdLogo from '../assets/masteredlogo-removebg-preview.png';
import MyResults from './MyResults';
import { initTawk, updateTawkAttributes, setSuppressTawkBubble, restoreDefaultWidgetIfNeeded } from '../services/tawk';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tooltip, Avatar, Box, Typography, Card, CardContent, LinearProgress, Chip } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotificationsBell from '../components/NotificationsBell';
import GroupLeaderboard from '../components/GroupLeaderboard';
import NewYearBanner from '../components/NewYearBanner';
import LeaksSpotlight from '../components/LeaksSpotlight';
import { getWeekKey, isDismissedForWeek, dismissForWeek, isSnoozed, snoozeHours } from '../utils/surveyReminder';

const Dashboard = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  
  const [completedIds, setCompletedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('SAT');
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [showSurveyReminder, setShowSurveyReminder] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [showRecentActivity, setShowRecentActivity] = useState(true);
  
  const studentId = localStorage.getItem('student_id') || 'Unknown ID';
  const studentName = localStorage.getItem('student_name') || 'Unknown Student';
  const firstName = studentName.split(' ')[0] || 'Student';

  // Time-based greeting
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  
  

  useEffect(() => {
    if (!authService.isLoggedIn()) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        setLoading(true);
        setActivityLoading(true);
        
        const availableTests = await testService.getAvailableTests();
        const myResultsResponse = await testService.getMyResults();
        const myResults = Array.isArray(myResultsResponse)
          ? myResultsResponse
          : myResultsResponse.Results ?? myResultsResponse.results ?? [];
        
        const doneIds = myResults.map(r => r.testId ?? r.TestId);
        setCompletedIds(doneIds);
        
        const filteredTests = availableTests.filter(t => t.isVisible && !doneIds.includes(t.id));
        setTests(filteredTests);
        
        // Build recent activity from results
        const activities = myResults.slice(0, 5).map(r => ({
          type: 'test_completed',
          testName: r.TestName || r.testName,
          date: r.CompletedAt || r.completedAt,
          score: `${r.CorrectCount || r.correctCount}/${(r.CorrectCount || r.correctCount) + (r.IncorrectCount || r.incorrectCount)}`,
          icon: <CheckCircleIcon sx={{ color: 'success.main' }} />
        }));
        setRecentActivity(activities);
        setError('');
      } catch (err) {
        setError('There was a problem loading tests. Please try again later.');
      } finally {
        setLoading(false);
        setActivityLoading(false);
      }
    };
    
    fetchTests();
  }, []);

  useEffect(() => {
    const loadSurveyStatus = async () => {
      try {
        setSurveyLoading(true);
        const studentPkStr = localStorage.getItem('student_pk');
        const sid = studentPkStr ? parseInt(studentPkStr, 10) : null;
        if (!sid) return;
        const status = await surveyService.getStatus(sid);
        setSurveyStatus(status);
      } catch (e) {
        // non-fatal
      } finally {
        setSurveyLoading(false);
      }
    };
    loadSurveyStatus();
  }, []);

  // Trigger reminder when survey is pending and not snoozed or dismissed for the week
  useEffect(() => {
    // Wait until survey status is loaded; avoid popping dialog on initial null
    if (!surveyStatus) return;
    const submitted = !!(surveyStatus.submittedThisWeek || surveyStatus.SubmittedThisWeek);
    if (submitted) return;
    if (isSnoozed()) return;
    const weekKey = getWeekKey(surveyStatus.weekStart || surveyStatus.WeekStart);
    if (!weekKey) return;
    if (isDismissedForWeek(weekKey)) return;
    setShowSurveyReminder(true);
  }, [surveyStatus]);

  const handleSurveyNow = () => {
    setShowSurveyReminder(false);
    navigate('/survey');
  };

  const handleSurveyLater = (hours = 6) => {
    snoozeHours(hours);
    setShowSurveyReminder(false);
  };

  const handleDismissThisWeek = () => {
    const weekKey = getWeekKey(surveyStatus?.weekStart || surveyStatus?.WeekStart);
    dismissForWeek(weekKey);
    setShowSurveyReminder(false);
  };

  // Initialize Tawk on Dashboard only
  useEffect(() => {
    try { restoreDefaultWidgetIfNeeded(); } catch {}
    setSuppressTawkBubble(false);
    const name = (studentName && studentName.trim()) ? studentName : (studentId ? `Student ${studentId}` : undefined);
    initTawk({
      visitor: name ? { name } : undefined,
      attributes: {
        page: 'dashboard',
        tab: activeTab,
      },
    });
    // No cleanup: keep widget available across app; tests page hides it via CSS body.no-tawk
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep Tawk attributes in sync with Dashboard tab
  useEffect(() => {
    updateTawkAttributes({ page: 'dashboard', tab: activeTab });
  }, [activeTab]);

  return (
    <div className="dashboard-container">
      <Dialog open={showSurveyReminder} onClose={() => setShowSurveyReminder(false)}>
        <DialogTitle>Weekly Teacher Survey</DialogTitle>
        <DialogContent>
          Please take 20 seconds to complete this week's survey about your Verbal and Math teachers.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleSurveyLater(6)}>Remind me later</Button>
          <Button onClick={handleDismissThisWeek}>Don't remind this week</Button>
          <Button onClick={handleSurveyNow} variant="contained">Complete now</Button>
        </DialogActions>
      </Dialog>
      <nav className="dashboard-nav">
        <img src={MasterEdLogo} alt="MasterEd Logo" className="bluebook-small-logo" />
        <div className="tab-buttons">
          <button className={`tab-btn ${activeTab === 'SAT' ? 'active' : ''}`} onClick={() => setActiveTab('SAT')}>WEEKLY TESTS</button>
          <button className={`tab-btn ${activeTab === 'Leaks' ? 'active' : ''}`} onClick={() => navigate('/leaks')}>PAST PAPERS</button>
        </div>
        <div className="nav-user">
          <NotificationsBell />
          <span className="user-name">{`${studentName} [ID: ${studentId}]`}</span>
          <Tooltip title="My Profile" arrow>
            <IconButton 
              onClick={() => navigate('/profile')} 
              sx={{ 
                color: '#333',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <AccountCircleIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
          <button className="logout-button" onClick={() => { authService.logout(); navigate('/login'); }}>Logout</button>
        </div>
      </nav>
      <main className="dashboard-content">
        <>
          {/* Personalized Greeting */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                {greeting}, {firstName}! 👋
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Ready to take your next SAT test?
              </Typography>
            </Box>

            {/* New Year Banner */}
            <NewYearBanner />

            {/* Group Leaderboard - Full Width */}
            <GroupLeaderboard />

            {/* Progress Metrics */}
            <Box sx={{ mb: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Tests Completed</Typography>
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? '...' : completedIds.length}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Available Tests</Typography>
                    <TrendingUpIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  </Box>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? '...' : tests.length}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Recent Activity */}
            {!activityLoading && recentActivity.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Recent Activity</Typography>
                    <Button 
                      size="small" 
                      onClick={() => setShowRecentActivity(!showRecentActivity)}
                      endIcon={showRecentActivity ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {showRecentActivity ? 'Hide' : 'Show'}
                    </Button>
                  </Box>
                  {showRecentActivity && (
                    <Box sx={{ mt: 2 }}>
                      {recentActivity.map((activity, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: idx < recentActivity.length - 1 ? '1px solid #eee' : 'none' }}>
                          {activity.icon}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>{activity.testName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(activity.date).toLocaleDateString()} • Score: {activity.score}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Survey Poster */}
            <section className="survey-poster">
              <div className="poster-card">
                <div className="poster-info">
                  <h2 className="poster-title">Weekly Teacher Survey</h2>
                  <p className="poster-desc">Tell us if you’re satisfied with your Verbal and Math teachers. It only takes 20 seconds.</p>
                </div>
                <div className="poster-actions">
                  <div className={`survey-status-pill ${surveyStatus?.submittedThisWeek || surveyStatus?.SubmittedThisWeek ? 'done' : 'pending'}`}>
                    {surveyLoading ? 'Checking…' : (surveyStatus?.submittedThisWeek || surveyStatus?.SubmittedThisWeek ? 'Submitted this week' : 'Pending this week')}
                  </div>
                  <button className="poster-cta" onClick={() => navigate('/survey')}>
                    {surveyStatus?.submittedThisWeek || surveyStatus?.SubmittedThisWeek ? 'View' : 'Complete Survey'}
                  </button>
                </div>
              </div>
            </section>
            <section className="available-tests">
              <h2>Available Tests</h2>
              
              {loading ? (
                <p>Loading tests...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : (
                <div className="tests-list">
                  {tests.length > 0 ? (
                    tests.map(test => (
                      <div key={test.id} className="test-card">
                        <h3>{test.name}</h3>
                        <div className="test-details">
                          <p><strong>Type:</strong> {test.type}</p>
                          <p><strong>Questions:</strong> {test.questions}</p>
                          <p><strong>Duration:</strong> 1 module</p>
                        </div>
                        <button
                          className="start-test-btn"
                          onClick={() => navigate(`/module/verbal/1?testId=${test.id}`)}
                          disabled={completedIds.includes(test.id)}
                        >
                          {completedIds.includes(test.id) ? 'Completed' : localStorage.getItem(`testProgress:${test.id}`) ? 'Resume Test' : 'Start Test'}
                        </button>
                      </div>
                    ))
                  ) : (
                    <p>No tests available at this time.</p>
                  )}
                </div>
              )}
            </section>
            <section className="past-results">
              <h2>Past Results</h2>
              <MyResults />
            </section>
        </>
      </main>
      
      {/* Past Papers Spotlight Overlay - only show when survey dialog is not active */}
      <LeaksSpotlight allowShow={!showSurveyReminder} />
    </div>
  );
};

export default Dashboard;
